import React from "react";
import Joi from "joi-browser";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { Form, Modal, Button, Badge, Spinner } from "react-bootstrap";
import { RiDeleteBin2Line } from "react-icons/ri";
import { getRouters, deleteRouter, addRouter, pingRouter } from "../services/routerService";
import FormComponent from "./common/formComponent";
import FloatingAddButton from "./common/floatingAddButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import PageLoading from "./common/pageLoading";
import Paper from "@material-ui/core/Paper";
import { StyledTableCell, StyledTableRow } from "../services/tableStyles";

class Routers extends FormComponent {
  state = {
    data: {
      name: "",
      role: "",
      radiusServerId: undefined,
      vendor: "",
      model: "",
      host: "",
      sshPort: "",
      username: "",
      password: "",
      pingTime: "Unreachable",
      oltGemIndex: undefined,
      oltVlan: undefined
    },
    errors: {},
    routers: [],
    radiusServers: [],
    routerToDelete: {},
    showAddModal: false,
    showConfirmModal: false,
    loading: "",
    processing: false,
  };

  constructor() {
    super();
    this.state.loading = true;
  }

  schema = {
    name: Joi.string().required().label("Name"),
    role: Joi.string().required().label("Role"),
    radiusServerId: Joi.string().label("Radius Server").when('role', { is: 'NAS', then: Joi.required()}),
    vendor: Joi.string().required().label("Vendor"),
    model: Joi.string().required().label("Model"),
    host: Joi.string().required().label("Host/IP"),
    sshPort: Joi.number().min(1).required().label("SSH Port"),
    username: Joi.string().required().label("Username"),
    password: Joi.string().required().label("Password"),
    pingTime: Joi.string().required().label("Ping Time"),
    oltGemIndex: Joi.number().min(0).label("OLT Gem Index").when('role', { is: 'NAS', then: Joi.required()}),
    oltVlan: Joi.number().min(1).label("OLT VLAN ID").when('role', { is: 'NAS', then: Joi.required()}),
  };

  doSubmit = async () => {
    this.setState({ processing: true });
    const { data: payload, routers: originalRouters } = this.state;

    try {
      const { data: newRouter } = await addRouter(payload);

      const routers = [...originalRouters];
      routers.unshift(newRouter);
      this.setState({ routers, showAddModal: false });
      toast.success("Router Added");
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
    this.setState({ processing: false });
  };

  handlePing = async (routerToPing) => {
    this.setState({ processing: true });
    toast.info("Ping Started");
    const { routers } = this.state;
    const index = routers.indexOf(routerToPing);

    const { data: router } = await pingRouter(routerToPing._id);
    routers[index] = router;
    if (router.pingTime === "Unreachable") {
      toast.error("Host Unreachable");
      this.setState({ processing: false });
    } else {
      toast.success("Ping Successful");
      this.setState({ processing: false });
    }

    this.setState({ routers });
  };

  handleDeleteRouter = async (routerId) => {
    this.setState({ processing: true });
    const { routers: originalRouters } = this.state;

    const routers = originalRouters.filter((r) => r._id !== routerId);
    this.setState({ routers, showConfirmModal: false });

    try {
      await deleteRouter(routerId);
      toast.success("Router Deleted");
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) toast.error(ex.response.data);
      this.setState({ routers: originalRouters });
    }
    this.setState({ processing: false });
  };

  showConfirmModal = (routerToDelete) => {
    this.setState({ showConfirmModal: true, routerToDelete });
  };

  handleShowAddModal = () => {
    this.setState({ showAddModal: true });
  };

  handleClose = () => {
    this.setState({ showAddModal: false, showConfirmModal: false });
  };

  async populateRouters() {
    try {
      const { data: routers } = await getRouters();
      this.setState({ routers });
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
    this.setState({ loading: false });
  }

  async componentDidMount() {
    await this.populateRouters();
  }

  render() {
    const { data, errors, routers, radiusServers, routerToDelete, showAddModal, showConfirmModal, loading, processing } = this.state;
    //const radiusServers = routers.filter((r) => r.role === "Radius Server");

    return (
      <Paper elevation={3}>
        <div className="card">
          <div className="card-header main-color-bg">
            <div>
              <h5>Routers</h5>
            </div>
          </div>
          <div className="card-body">
            <span className="card-text">
              {loading && <PageLoading loading={loading} />}
              {!loading && routers.length === 0 && <span>No Routers to Display</span>}
              {!loading && (
                <FloatingAddButton classes="float-right no-border mt-2 mb-3" onClick={this.handleShowAddModal} />
              )}
              {!loading && routers.length > 0 && (
                <TableContainer component={Paper}>
                  <Table aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Name</StyledTableCell>
                        <StyledTableCell align="right">Role</StyledTableCell>
                        <StyledTableCell align="right">Host/IP</StyledTableCell>
                        <StyledTableCell align="right">SSH Port</StyledTableCell>
                        <StyledTableCell align="right"></StyledTableCell>
                        <StyledTableCell align="right"></StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {routers.map((router) => (
                        <StyledTableRow key={router._id}>
                          <StyledTableCell component="th" scope="row">
                            <Link to={`/routers/${router._id}`}>{router.name}</Link>
                          </StyledTableCell>
                          <StyledTableCell align="right">{router.role}</StyledTableCell>
                          <StyledTableCell align="right">
                            <kbd>{router.host}</kbd>
                          </StyledTableCell>
                          <StyledTableCell align="right">{router.sshPort}</StyledTableCell>
                          <StyledTableCell align="right">
                            <Button disabled={processing} onClick={() => this.handlePing(router)} variant="success">
                              {processing && (
                                <Spinner
                                  as="span"
                                  className="mr-2"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                />
                              )}
                              Ping:{" "}
                              <Badge variant={`${router.pingTime === "Unreachable" ? "danger" : "warning"}`}>
                                {router.pingTime}
                              </Badge>
                            </Button>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Button
                              onClick={() =>
                                this.showConfirmModal({
                                  _id: router._id,
                                  name: router.name,
                                })
                              }
                              variant="danger"
                            >
                              <RiDeleteBin2Line />
                            </Button>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </span>
          </div>
        </div>

        <Modal show={showAddModal} onHide={this.handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Add Router</Modal.Title>
          </Modal.Header>
          <Form onSubmit={this.handleSubmit}>
            <Modal.Body>
              <Form.Group controlId="name">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  name="name"
                  onChange={this.handleChange}
                  value={data.name}
                  size="sm"
                  type="text"
                  autoFocus
                />
                {errors.name && <span className="text-danger">{errors.name}</span>}
              </Form.Group>

              <Form.Group controlId="role">
                <Form.Label>Role</Form.Label>
                <Form.Control as="select" name="role" value={data.role} onChange={this.handleRouterRoleSelect}>
                  <option></option>
                  <option>Aggregator</option>
                  <option>BGP</option>
                  <option>NAS</option>
                  <option>Radius Server</option>
                </Form.Control>
                {errors.role && <span className="text-danger">{errors.role}</span>}
              </Form.Group>

              {radiusServers.length > 0 && <Form.Group controlId="radiusServerId">
                <Form.Label>Radius Server</Form.Label>
                <Form.Control as="select" name="radiusServerId" value={data.radiusServerId} onChange={this.handleChange}>
                <option value="">Select Radius Server</option>
                  {radiusServers.map((radius) => (
                    <option key={radius._id} value={radius._id}>
                      {radius.name} ({radius.host})
                    </option>
                  ))}
                </Form.Control>
                {errors.radiusServerId && <span className="text-danger">{errors.radiusServerId}</span>}
              </Form.Group>}

              {radiusServers.length > 0 && <Form.Group controlId="oltGemIndex">
                <Form.Label>OLT GEM Index</Form.Label>
                <Form.Control name="oltGemIndex" onChange={this.handleChange} value={data.oltGemIndex} size="sm" type="number" />
                {errors.oltGemIndex && <span className="text-danger">{errors.oltGemIndex}</span>}
              </Form.Group>}

              {radiusServers.length > 0 && <Form.Group controlId="oltVlan">
                <Form.Label>OLT VLAN ID</Form.Label>
                <Form.Control name="oltVlan" onChange={this.handleChange} value={data.oltVlan} size="sm" type="number" />
                {errors.oltVlan && <span className="text-danger">{errors.oltVlan}</span>}
              </Form.Group>}

              <Form.Group controlId="vendor">
                <Form.Label>Vendor</Form.Label>
                <Form.Control name="vendor" onChange={this.handleChange} value={data.vendor} size="sm" type="text" />
                {errors.vendor && <span className="text-danger">{errors.vendor}</span>}
              </Form.Group>

              <Form.Group controlId="model">
                <Form.Label>Model</Form.Label>
                <Form.Control name="model" onChange={this.handleChange} value={data.model} size="sm" type="text" />
                {errors.model && <span className="text-danger">{errors.model}</span>}
              </Form.Group>

              <Form.Group controlId="host">
                <Form.Label>Host/IP</Form.Label>
                <Form.Control name="host" onChange={this.handleChange} value={data.host} size="sm" type="text" />
                {errors.host && <span className="text-danger">{errors.host}</span>}
              </Form.Group>

              <Form.Group controlId="sshPort">
                <Form.Label>SSH Port</Form.Label>
                <Form.Control name="sshPort" onChange={this.handleChange} value={data.sshPort} size="sm" type="text" />
                {errors.sshPort && <span className="text-danger">{errors.sshPort}</span>}
              </Form.Group>

              <Form.Group controlId="username">
                <Form.Label>User</Form.Label>
                <Form.Control name="username" onChange={this.handleChange} value={data.username} size="sm" type="text" />
                {errors.username && <span className="text-danger">{errors.username}</span>}
              </Form.Group>

              <Form.Group controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  name="password"
                  onChange={this.handleChange}
                  value={data.password}
                  size="sm"
                  type="password"
                />
                {errors.password && <span className="text-danger">{errors.password}</span>}
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button onClick={this.handleClose} variant="secondary">
                Close
              </Button>
              <Button disabled={this.validate() || processing} type="submit" variant="primary">
                {processing && (
                  <Spinner as="span" className="mr-2" animation="border" size="sm" role="status" aria-hidden="true" />
                )}
                Add
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showConfirmModal} size="md" centered onHide={this.handleClose}>
          <Modal.Header closeButton className="bg-danger text-white">
            <Modal.Title>Delete Router</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to delete the router: {routerToDelete.name}?</Modal.Body>

          <Modal.Footer>
            <Button onClick={this.handleClose} variant="secondary">
              No
            </Button>
            <Button
              disabled={processing}
              onClick={() => this.handleDeleteRouter(routerToDelete._id)}
              type="submit"
              variant="primary"
            >
              {processing && (
                <Spinner as="span" className="mr-2" animation="border" size="sm" role="status" aria-hidden="true" />
              )}
              Yes
            </Button>
          </Modal.Footer>
        </Modal>
      </Paper>
    );
  }
}

export default Routers;
